import axios from 'axios';
import { createProductModel } from '../models/productModel';
import { createUserModel } from '../models/userModel';

// ============================================================
//  BASE CONFIGURATION — Centralized production backend
// ============================================================
export const BASE_URL = 'https://munoapi.brainexworld.com/api/';

export const ENDPOINTS = {
  USER: {
    LOGIN: 'User/login',
    REGISTER: 'User/register',
    GET_PROFILE: 'User/getProfile',
    GET_ALL: 'User/getallusers',
    UPDATE: (id) => `User/update/${id}`,
    DELETE: (id) => `User/delete/${id}`,
    FORGOT_PASSWORD: 'User/forgot-password',
    RESET_PASSWORD: 'User/reset-password',
  },
  PRODUCT: {
    GET_ALL: `Product/getAllProducts?limit=9999&page=1&v=${Date.now()}`,
    GET_ONE: (id) => `Product/getProduct/${id}`,
    NEW_ARRIVALS: 'Product/new-arrivals?limit=9999',
    TOP_RATED: 'Product/top-rated?limit=9999',
    CREATE: 'Product/createProduct',
    UPDATE: (id) => `Product/updateProduct/${id}`,
    DELETE: (id) => `Product/deleteProduct/${id}`,
  },
  CATEGORY: {
    GET_ALL: 'Category/getAllCategories',
    CREATE: 'Category/createCategory',
    UPDATE: (id) => `Category/UpdateCategory/${id}`,
    DELETE: (id) => `Category/deleteCategory/${id}`,
  },
  CART: {
    GET: 'Cart/get',
    ADD: 'Cart/items',
    UPDATE: (id) => `Cart/items/${id}`,
    REMOVE: (id) => `Cart/items/${id}`,
  },
  ORDER: {
    PLACE: 'Order/placeOrder',
    MY_ORDERS: 'Order/my-orders',
    GET_ALL: 'Order/getAllOrders',
    STATUS: (id) => `Order/status/${id}`,
    CANCEL: (id) => `Order/cancel/${id}`,
    DELETE: (id) => `Order/delete/${id}`,
  },
  SLIDER: {
    GET: 'slider/get',
    CREATE: 'slider/create',
    UPDATE: (id) => `slider/update/${id}`,
    DELETE: (id) => `slider/delete/${id}`,
  },
  REVIEW: {
    ADD: 'review/addReview',
    MY: 'review/my-reviews',
    GET: (productId) => `review/getReviews/${productId}`,
    DELETE: (id) => `review/deleteReview/${id}`,
  },
  SALE: {
    ACTIVE: 'sale/active',
    CREATE: 'sale/CreateSale',
    UPDATE: (id) => `sale/update/${id}`,
    DELETE: (id) => `sale/delete/${id}`,
  },
  INVENTORY: {
    STATS: 'inventory/getinventory',
    LOW_STOCK: 'inventory/low-products',
    LOW_VARIANTS: 'inventory/low-variants',
    OUT_OF_STOCK: 'inventory/out-of-stock',
    ALL: 'inventory/all',
  },
  DASHBOARD: {
    DATA: 'adminDashboard/dashboard',
  },
  WISHLIST: {
    GET: 'wishList/getWishList',
    ADD: 'wishList/addToWish',
    REMOVE: (productId) => `wishList/removeFromWish/${productId}`,
  },
};

const API = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Automatic token injection
API.interceptors.request.use((config) => {
  try {
    const token = sessionStorage.getItem('token');
    if (token && token !== 'undefined' && token !== 'null') {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn('[API] Token retrieval failed:', err.message);
  }
  return config;
}, (error) => Promise.reject(error));

// ============================================================
//  CONSOLIDATED SERVICES — One source for all API logic
// ============================================================

export const userService = {
  async login(email, password) {
    try {
      const response = await API.post(ENDPOINTS.USER.LOGIN, { email, password });
      const rawData = response.data.data;
      if (!rawData) throw new Error('Invalid response from the server.');
      return createUserModel({ ...rawData, token: rawData.token });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed.');
    }
  },
  async register(fullName, email, password, role = 'customer') {
    try {
      const response = await API.post(ENDPOINTS.USER.REGISTER, { fullName, email, password, role });
      const rawData = response.data.data;
      if (!rawData) throw new Error('Invalid response.');
      return createUserModel({ user: rawData.user, token: rawData.token });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Registration failed.');
    }
  },
  async getProfile() {
    const res = await API.get(ENDPOINTS.USER.GET_PROFILE);
    return res.data;
  },
  async getAllUsers() {
    const res = await API.get(ENDPOINTS.USER.GET_ALL);
    return res.data;
  },
  async updateProfile(id, userData) {
    const res = await API.put(ENDPOINTS.USER.UPDATE(id), userData);
    return res.data;
  },
  async deleteUser(id) {
    const res = await API.delete(ENDPOINTS.USER.DELETE(id));
    return res.data;
  },
  async forgotPassword(email) {
    const res = await API.post(ENDPOINTS.USER.FORGOT_PASSWORD, { email });
    return res.data;
  },
  async resetPassword(data) {
    const res = await API.post(ENDPOINTS.USER.RESET_PASSWORD, data);
    return res.data;
  }
};

export const productService = {
  getAllProducts: async () => {
    const CACHE_KEY = 'ella_products_cache';
    try {
      let payload = null;
      try {
        const response = await API.get(ENDPOINTS.PRODUCT.GET_ALL);
        payload = response.data;
        localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
      } catch (apiErr) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) payload = JSON.parse(cached);
        else return [];
      }

      let products = [];
      if (Array.isArray(payload)) products = payload;
      else if (Array.isArray(payload?.products)) products = payload.products;
      else if (Array.isArray(payload?.data)) products = payload.data;
      else if (Array.isArray(payload?.data?.products)) products = payload.data.products;
      else if (Array.isArray(payload?.docs)) products = payload.docs;
      else if (Array.isArray(payload?.data?.docs)) products = payload.data.docs;

      const localFlags = JSON.parse(localStorage.getItem('ella_product_flags') || '{}');
      const storedDescriptions = JSON.parse(localStorage.getItem('ella_product_descriptions') || '{}');

      return products.map(item => {
        const id = item._id || item.id;
        const model = createProductModel(item);
        model.description = model.description || storedDescriptions[id] || "No description available";
        if (localFlags[id]) Object.assign(model, localFlags[id]);
        return model;
      });
    } catch (error) {
      console.error('Fatal getAllProducts Error:', error.message);
      return [];
    }
  },
  getProductById: async (id) => {
    const all = await productService.getAllProducts();
    return all.find(p => (p._id || p.id) === id) || null;
  },
  createProduct: async (productData) => {
    const res = await API.post(ENDPOINTS.PRODUCT.CREATE, productData);
    return res.data;
  },
  updateProduct: async (id, productData) => {
    const res = await API.put(ENDPOINTS.PRODUCT.UPDATE(id), productData);
    return res.data;
  },
  deleteProduct: async (id) => {
    const res = await API.delete(ENDPOINTS.PRODUCT.DELETE(id));
    return res.data;
  }
};

export const cartService = {
  getCart: async () => {
    try {
      const response = await API.get(ENDPOINTS.CART.GET);
      return response.data;
    } catch (error) {
      return null;
    }
  },
  addToCart: async (product, variantId, quantity = 1) => {
    const productId = product?._id || product?.id || product;
    const payload = { productId, qty: quantity };
    if (variantId && variantId !== 'mock-variant-id') payload.variantId = variantId;
    const response = await API.post(ENDPOINTS.CART.ADD, payload);
    return response.data;
  },
  updateCartItem: async (itemId, qty) => {
    const response = await API.put(ENDPOINTS.CART.UPDATE(itemId), { qty });
    return response.data;
  },
  removeFromCart: async (itemId) => {
    const response = await API.delete(ENDPOINTS.CART.REMOVE(itemId));
    return response.data;
  }
};

export const categoryService = {
  getAllCategories: async () => {
    const response = await API.get(ENDPOINTS.CATEGORY.GET_ALL);
    return Array.isArray(response.data) ? response.data : response.data.data || [];
  },
  createCategory: async (data) => (await API.post(ENDPOINTS.CATEGORY.CREATE, data)).data,
  updateCategory: async (id, data) => (await API.put(ENDPOINTS.CATEGORY.UPDATE(id), data)).data,
  deleteCategory: async (id) => (await API.delete(ENDPOINTS.CATEGORY.DELETE(id))).data
};

export const orderService = {
  placeOrder: async (data) => (await API.post(ENDPOINTS.ORDER.PLACE, data)).data,
  getMyOrders: async () => {
    try {
      const res = await API.get(ENDPOINTS.ORDER.MY_ORDERS);
      return res.data;
    } catch (e) { return []; }
  },
  getAllOrders: async () => {
    try {
      const res = await API.get(ENDPOINTS.ORDER.GET_ALL);
      return res.data;
    } catch (e) { return []; }
  },
  updateOrderStatus: async (id, status, remarks = '') => (await API.patch(ENDPOINTS.ORDER.STATUS(id), { status, remarks })).data,
  cancelOrder: async (id) => (await API.put(ENDPOINTS.ORDER.CANCEL(id), {})).data,
  deleteOrder: async (id) => (await API.delete(ENDPOINTS.ORDER.DELETE(id))).data
};

export const sliderService = {
  getSliders: async () => {
    try {
      const res = await API.get(ENDPOINTS.SLIDER.GET);
      const data = res.data?.sliders || res.data?.data || res.data;
      return Array.isArray(data) ? data : [];
    } catch (e) { return []; }
  },
  createSlider: async (data) => (await API.post(ENDPOINTS.SLIDER.CREATE, data)).data,
  updateSlider: async (id, data) => (await API.put(ENDPOINTS.SLIDER.UPDATE(id), data)).data,
  deleteSlider: async (id) => (await API.delete(ENDPOINTS.SLIDER.DELETE(id))).data
};

export const reviewService = {
  addReview: async (data) => (await API.post(ENDPOINTS.REVIEW.ADD, data)).data,
  getMyReviews: async () => {
    try {
      const res = await API.get(ENDPOINTS.REVIEW.MY);
      return res.data;
    } catch (e) { return []; }
  },
  getReviewsByProduct: async (id) => {
    try {
      const res = await API.get(ENDPOINTS.REVIEW.GET(id));
      return res.data;
    } catch (e) { return []; }
  },
  deleteReview: async (id) => (await API.delete(ENDPOINTS.REVIEW.DELETE(id))).data,
  getReviewsForProduct: async (id) => {
    try {
      const res = await API.get(ENDPOINTS.REVIEW.GET(id));
      return res.data;
    } catch (e) { return []; }
  },
  getGlobalReviews: async (productIds = []) => {
    if (!productIds || productIds.length === 0) return [];
    try {
      const sampleIds = productIds.slice(0, 12);
      const allResponses = await Promise.all(
        sampleIds.map(id => API.get(ENDPOINTS.REVIEW.GET(id), { silent: true }).catch(err => {
          if (err.response?.status !== 401) {
            console.debug('Review fetch skipped (Auth required or Error)');
          }
          return null;
        }))
      );
      let aggregatedReviews = [];
      allResponses.forEach(resp => {
        if (!resp || !resp.data) return;
        const data = resp.data.payload || resp.data.reviews || resp.data.data || (Array.isArray(resp.data) ? resp.data : []);
        if (Array.isArray(data)) aggregatedReviews = [...aggregatedReviews, ...data];
      });
      return aggregatedReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (err) { return []; }
  }
};

export const wishlistService = {
  getWishlist: async () => {
    try {
      const res = await API.get(ENDPOINTS.WISHLIST.GET);
      return res.data;
    } catch (e) { return []; }
  },
  addToWishlist: async (productId) => (await API.post(ENDPOINTS.WISHLIST.ADD, { productId })).data,
  removeFromWishlist: async (productId) => (await API.delete(ENDPOINTS.WISHLIST.REMOVE(productId))).data
};

export const saleService = {
  getActiveSale: async () => {
    try {
      const res = await API.get(ENDPOINTS.SALE.ACTIVE);
      const payload = res.data;
      return payload?.data || payload?.sale || (Array.isArray(payload) ? payload[0] : payload);
    } catch (e) { return null; }
  },
  getAllSales: async () => {
    try {
      const res = await API.get(ENDPOINTS.SALE.ACTIVE);
      const sale = res.data?.data;
      if (!sale) return [];
      return Array.isArray(sale) ? sale : [sale];
    } catch (e) { return []; }
  },
  createSale: async (data) => (await API.post(ENDPOINTS.SALE.CREATE, data)).data,
  updateSale: async (id, data) => (await API.put(ENDPOINTS.SALE.UPDATE(id), data)).data,
  deleteSale: async (id) => (await API.delete(ENDPOINTS.SALE.DELETE(id))).data
};

export const inventoryService = {
  getInventoryStats: async () => (await API.get(ENDPOINTS.INVENTORY.STATS)).data,
  getLowStockProducts: async () => { try { return (await API.get(ENDPOINTS.INVENTORY.LOW_STOCK)).data; } catch (e) { return []; } },
  getLowStockVariants: async () => { try { return (await API.get(ENDPOINTS.INVENTORY.LOW_VARIANTS)).data; } catch (e) { return []; } },
  getOutOfStockProducts: async () => { try { return (await API.get(ENDPOINTS.INVENTORY.OUT_OF_STOCK)).data; } catch (e) { return []; } },
  getAllInventory: async () => { try { return (await API.get(ENDPOINTS.INVENTORY.ALL)).data; } catch (e) { return []; } }
};

export const dashboardService = {
  getDashboardData: async () => {
    try { return (await API.get(ENDPOINTS.DASHBOARD.DATA)).data; } catch (e) { return null; }
  }
};

// --- DERIVED SERVICES ---
export const topRatedService = {
  getTopRated: async () => {
    try {
      const all = await productService.getAllProducts();
      let filtered = all.filter(p => Boolean(p.isTopRated) && p.isActive !== false);
      if (filtered.length === 0) {
        filtered = all.filter(p => p.isActive !== false).sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0)).slice(0, 8);
      }
      return filtered;
    } catch (e) { return []; }
  }
};

export const newArrivalService = {
  getNewArrivals: async () => {
    try {
      const all = await productService.getAllProducts();
      let filtered = all.filter(p => Boolean(p.isNewArrival) && p.isActive !== false);
      if (filtered.length === 0) {
        filtered = all.filter(p => p.isActive !== false).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8);
      }
      return filtered;
    } catch (e) { return []; }
  }
};

export const bestSellingService = {
  getBestSellers: async () => {
    try {
      const all = await productService.getAllProducts();
      let filtered = all.filter(p => Boolean(p.isBestSelling) && p.isActive !== false);
      if (filtered.length === 0) {
        filtered = all.filter(p => p.isActive !== false).slice(0, 8);
      }
      return filtered;
    } catch (e) { return []; }
  }
};

export default API;

